import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["typeInput", "textArea", "visualTextArea", "textAreaContainer", "fileInput", "submitButton", "plusButton", "mediaDropdown", "imageOption", "audioOption", "documentOption", "audioRecorder"]
  static values = {
    chatroomId: String,
    textUrl: String,
    imageUrl: String,
    audioUrl: String,
    documentUrl: String
  }

  connect() {
    this.currentType = 'text'
    this.isDropdownOpen = false
    this.updateForm()

    // Setup visual textarea
    this.setupVisualTextarea()

    // Preserve content before Turbo replaces the form
    this.preserveContentBeforeReplace()

    // Restore content after Turbo replaces the form
    this.restoreContentAfterReplace()

    // Clear content after successful form submission
    this.clearContentAfterSubmit()

    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this))
  }

  setupVisualTextarea() {
    if (this.hasVisualTextAreaTarget) {
      // Sync content between visual and hidden textarea
      this.visualTextAreaTarget.addEventListener('input', () => {
        this.textAreaTarget.value = this.getTextWithLineBreaks(this.visualTextAreaTarget)
      })

      // Submit on Enter, new line on Shift+Enter
      this.visualTextAreaTarget.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          // Sync content to hidden textarea before submitting
          this.textAreaTarget.value = this.getTextWithLineBreaks(this.visualTextAreaTarget)
          const form = this.element.querySelector('form')
          if (form) {
            form.requestSubmit()
          }
        }
      })

      // Handle placeholder
      this.visualTextAreaTarget.addEventListener('focus', () => {
        if (this.visualTextAreaTarget.textContent.trim() === '') {
          this.visualTextAreaTarget.classList.add('placeholder')
        } else {
          this.visualTextAreaTarget.classList.remove('placeholder')
        }
      })

      this.visualTextAreaTarget.addEventListener('blur', () => {
        if (this.visualTextAreaTarget.textContent.trim() === '') {
          this.visualTextAreaTarget.classList.add('placeholder')
        } else {
          this.visualTextAreaTarget.classList.remove('placeholder')
        }
      })

    }
  }

  saveContentToStorage() {
    if (this.hasVisualTextAreaTarget) {
      const chatroomId = this.chatroomIdValue
      const textContent = this.visualTextAreaTarget.textContent.trim()

      // Solo guardar si hay contenido (guardar textContent para evitar XSS)
      if (textContent) {
        sessionStorage.setItem(`preserved_message_content_${chatroomId}`, textContent)
      } else {
        // Si está vacío, limpiar el storage
        sessionStorage.removeItem(`preserved_message_content_${chatroomId}`)
      }
    }
  }

  restoreContent(preservedContent, chatroomId) {
    if (!this.hasVisualTextAreaTarget) {
      console.warn('⚠️ No hay visualTextAreaTarget para restaurar')
      return
    }

    const visualTextArea = this.visualTextAreaTarget

    // El contenido preservado ya es texto plano (textContent), no HTML
    // Esto previene stored XSS attacks
    const textContent = preservedContent.trim()

    if (!textContent) {
      console.warn('⚠️ No hay contenido para restaurar')
      return
    }

    // Limpiar placeholder class
    visualTextArea.classList.remove('placeholder')

    // Limpiar el elemento completamente
    while (visualTextArea.firstChild) {
      visualTextArea.removeChild(visualTextArea.firstChild)
    }

    // Restaurar usando textContent directamente (más confiable para contenteditable)
    visualTextArea.textContent = textContent

    // Asegurarse de que no tenga la clase placeholder
    visualTextArea.classList.remove('placeholder')

    // Sincronizar con el textarea oculto
    if (this.hasTextAreaTarget) {
      this.textAreaTarget.value = textContent
    }

    // Disparar evento input para sincronización completa
    visualTextArea.dispatchEvent(new Event('input', { bubbles: true }))

    // Verificar después de un delay
    setTimeout(() => {
      if (visualTextArea.textContent.trim()) {
        sessionStorage.removeItem(`preserved_message_content_${chatroomId}`)
      }
    }, 100)
  }

  disconnect() {
    // Limpiar event listeners para prevenir memory leaks
    const outsideClickHandler = this.handleOutsideClick.bind(this)
    document.removeEventListener('click', outsideClickHandler)

    if (this.preserveContentHandler) {
      document.removeEventListener('turbo:before-stream-render', this.preserveContentHandler)
      document.removeEventListener('turbo:before-render', this.preserveContentHandler)
      this.preserveContentHandler = null
    }

    if (this.restoreContentHandler) {
      document.removeEventListener('turbo:after-stream-render', this.restoreContentHandler)
      document.removeEventListener('turbo:after-render', this.restoreContentHandler)
      document.removeEventListener('turbo:frame-load', this.restoreContentHandler)
      this.restoreContentHandler = null
    }

    if (this.clearContentHandler) {
      document.removeEventListener('turbo:submit-end', this.clearContentHandler)
      this.clearContentHandler = null
    }
  }

  preserveContentBeforeReplace() {
    // Limpiar handlers anteriores si existen (para prevenir memory leaks en reconexiones)
    if (this.preserveContentHandler) {
      document.removeEventListener('turbo:before-stream-render', this.preserveContentHandler)
      document.removeEventListener('turbo:before-render', this.preserveContentHandler)
    }

    // Escuchar eventos de Turbo antes de que reemplace el formulario
    this.preserveContentHandler = () => {
      this.saveContentToStorage()
    }

    document.addEventListener('turbo:before-stream-render', this.preserveContentHandler)
    document.addEventListener('turbo:before-render', this.preserveContentHandler)
  }

  restoreContentAfterReplace() {
    const chatroomId = this.chatroomIdValue

    // Limpiar handlers anteriores si existen (para prevenir memory leaks en reconexiones)
    if (this.restoreContentHandler) {
      document.removeEventListener('turbo:after-stream-render', this.restoreContentHandler)
      document.removeEventListener('turbo:after-render', this.restoreContentHandler)
      document.removeEventListener('turbo:frame-load', this.restoreContentHandler)
    }

    const checkAndRestore = () => {
      const preservedContent = sessionStorage.getItem(`preserved_message_content_${chatroomId}`)
      if (preservedContent && this.hasVisualTextAreaTarget && !this.visualTextAreaTarget.textContent.trim()) {
        this.restoreContent(preservedContent, chatroomId)
        return true
      }
      return false
    }

    // Intentar restaurar inmediatamente
    if (checkAndRestore()) return

    // Intentar después de delays cuando Turbo termine de renderizar
    this.restoreContentHandler = () => {
      setTimeout(checkAndRestore, 100)
    }

    document.addEventListener('turbo:after-stream-render', this.restoreContentHandler)
    document.addEventListener('turbo:after-render', this.restoreContentHandler)
    document.addEventListener('turbo:frame-load', this.restoreContentHandler)

    setTimeout(checkAndRestore, 100)
  }

  clearContentAfterSubmit() {
    // Limpiar handler anterior si existe (para prevenir memory leaks en reconexiones)
    if (this.clearContentHandler) {
      document.removeEventListener('turbo:submit-end', this.clearContentHandler)
    }

    this.clearContentHandler = (event) => {
      // Verificar que es este formulario el que se envió
      const form = event.target
      if (!this.element.contains(form)) return

      // Verificar que fue exitoso (sin errores de validación)
      if (event.detail.success) {
        const chatroomId = this.chatroomIdValue

        // Limpiar sessionStorage para prevenir restauración
        sessionStorage.removeItem(`preserved_message_content_${chatroomId}`)

        // Limpiar el visual textarea
        if (this.hasVisualTextAreaTarget) {
          this.visualTextAreaTarget.textContent = ''
          this.visualTextAreaTarget.classList.add('placeholder')
        }

        // Limpiar el textarea oculto
        if (this.hasTextAreaTarget) {
          this.textAreaTarget.value = ''
        }

        // Resetear el tipo de mensaje a texto
        this.currentType = 'text'
        if (this.hasTypeInputTarget) {
          this.typeInputTarget.value = 'text'
        }

        // Limpiar file input si existe
        if (this.hasFileInputTarget) {
          this.fileInputTarget.value = ''
        }

        // Remover preview de archivos si existe
        this.removeFilePreview()

        // Actualizar el formulario
        this.updateForm()
      }
    }

    document.addEventListener('turbo:submit-end', this.clearContentHandler)
  }

  handleOutsideClick(event) {
    if (this.isDropdownOpen && !this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  toggleMediaOptions(event) {
    event.preventDefault()
    this.isDropdownOpen = !this.isDropdownOpen

    if (this.isDropdownOpen) {
      this.mediaDropdownTarget.style.display = 'block'
      this.plusButtonTarget.classList.add('active')
      this.positionDropdown()
    } else {
      this.mediaDropdownTarget.style.display = 'none'
      this.plusButtonTarget.classList.remove('active')
    }
  }

  positionDropdown() {
    const buttonRect = this.plusButtonTarget.getBoundingClientRect()
    const dropdown = this.mediaDropdownTarget

    // Apply all styles directly in JavaScript to ensure they work
    dropdown.style.position = 'fixed'
    dropdown.style.top = `${buttonRect.top - dropdown.offsetHeight - 8}px`
    dropdown.style.left = `${buttonRect.left}px`
    dropdown.style.zIndex = '1000'
    dropdown.style.background = 'white'
    dropdown.style.border = '1px solid #e1e5e9'
    dropdown.style.borderRadius = '12px'
    dropdown.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)'
    dropdown.style.minWidth = '140px'
    dropdown.style.padding = '8px'
    dropdown.style.overflow = 'hidden'
    dropdown.style.animation = 'dropdownFadeIn 0.2s ease-out'
    dropdown.style.transformOrigin = 'top center'

    // Apply styles to each option
    const options = dropdown.querySelectorAll('.media-option')
    options.forEach((option, index) => {
      option.style.display = 'flex'
      option.style.alignItems = 'center'
      option.style.padding = '12px 16px'
      option.style.cursor = 'pointer'
      option.style.transition = 'all 0.2s ease'
      option.style.position = 'relative'

      // First option rounded top
      if (index === 0) {
        option.style.borderRadius = '12px 12px 0 0'
      }
      // Last option rounded bottom
      if (index === options.length - 1) {
        option.style.borderRadius = '0 0 12px 12px'
      }
      // Only one option - fully rounded
      if (options.length === 1) {
        option.style.borderRadius = '12px'
      }

      // Style the icon
      const icon = option.querySelector('i')
      if (icon) {
        icon.style.marginRight = '12px'
        icon.style.color = '#6c757d'
        icon.style.width = '18px'
        icon.style.fontSize = '16px'
        icon.style.transition = 'all 0.2s ease'
        icon.style.textAlign = 'center'
      }

      // Style the text
      const span = option.querySelector('span')
      if (span) {
        span.style.fontSize = '14px'
        span.style.color = '#333'
        span.style.fontWeight = '500'
        span.style.transition = 'color 0.2s ease'
      }

      // Add hover effect
      option.addEventListener('mouseenter', () => {
        option.style.background = '#f0f8ff'
        if (icon) icon.style.color = '#25d366'
        if (icon) icon.style.transform = 'scale(1.1)'
        if (span) span.style.color = '#25d366'
      })

      option.addEventListener('mouseleave', () => {
        option.style.background = 'transparent'
        if (icon) icon.style.color = '#6c757d'
        if (icon) icon.style.transform = 'scale(1)'
        if (span) span.style.color = '#333'
      })

      // Add active effect
      option.addEventListener('mousedown', () => {
        option.style.background = '#e8f5e8'
        option.style.transform = 'scale(0.98)'
      })

      option.addEventListener('mouseup', () => {
        option.style.transform = 'scale(1)'
      })
    })
  }

  selectImage(event) {
    event.preventDefault()
    this.currentType = 'image'
    this.typeInputTarget.value = 'image'
    this.hideDropdown()

    // Set file input to accept images only
    this.fileInputTarget.accept = 'image/*'

    // Trigger file input
    this.fileInputTarget.click()
  }

  selectAudio(event) {
    event.preventDefault()
    this.currentType = 'audio'
    this.typeInputTarget.value = 'audio'
    this.hideDropdown()

    // Show audio recorder interface
    this.showAudioRecorder()
  }

  selectDocument(event) {
    event.preventDefault()
    this.currentType = 'document'
    this.typeInputTarget.value = 'document'
    this.hideDropdown()

    // Set file input to accept documents only
    this.fileInputTarget.accept = '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx'

    // Trigger file input
    this.fileInputTarget.click()
  }

  hideDropdown() {
    this.isDropdownOpen = false
    this.mediaDropdownTarget.style.display = 'none'
    this.plusButtonTarget.classList.remove('active')
  }

  showAudioRecorder() {
    if (this.hasAudioRecorderTarget) {
      this.audioRecorderTarget.style.display = 'block'
      this.textAreaContainerTarget.style.display = 'none'

      // Make textarea not required when recording audio
      if (this.hasTextAreaTarget) {
        this.textAreaTarget.required = false
      }
    }
  }

  hideAudioRecorder() {
    if (this.hasAudioRecorderTarget) {
      this.audioRecorderTarget.style.display = 'none'
      this.textAreaContainerTarget.style.display = 'block'

      // Update form state after hiding audio recorder
      this.updateForm()
    }
  }

  updateForm() {
    // Update visual textarea based on current state
    if (this.hasVisualTextAreaTarget) {
      if (this.visualTextAreaTarget.querySelector('.file-preview-inline')) {
        // When there's a file preview, text area is optional
        let placeholder = 'Escribe tu mensaje...'
        if (this.currentType === 'image') {
          placeholder = 'Descripción de la imagen (opcional)'
        } else if (this.currentType === 'audio') {
          placeholder = 'Descripción del audio (opcional)'
        } else if (this.currentType === 'document') {
          placeholder = 'Descripción del documento (opcional)'
        }
        this.visualTextAreaTarget.setAttribute('placeholder', placeholder)
        this.textAreaTarget.required = false
      } else {
        // When no file, text is required
        this.visualTextAreaTarget.setAttribute('placeholder', 'Escribe tu mensaje...')
        this.textAreaTarget.required = true
      }
    }

    // Special handling for audio type - make textarea not required
    if (this.currentType === 'audio') {
      this.textAreaTarget.required = false
    }

    this.updateSubmitButton()
  }

  updateSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      switch(this.currentType) {
        case 'text':
          this.submitButtonTarget.textContent = 'Enviar'
          this.submitButtonTarget.className = 'btn btn-primary btn-send'
          break
        case 'image':
          this.submitButtonTarget.textContent = 'Enviar imagen'
          this.submitButtonTarget.className = 'btn btn-success btn-send'
          break
        case 'audio':
          this.submitButtonTarget.textContent = 'Enviar audio'
          this.submitButtonTarget.className = 'btn btn-warning btn-send'
          break
        case 'document':
          this.submitButtonTarget.textContent = 'Enviar documento'
          this.submitButtonTarget.className = 'btn btn-info btn-send'
          break
      }
    }
  }

  handleFileChange(event) {
    const file = event.target.files[0]
    if (file) {
      // Update filename input if it's a document
      if (this.currentType === 'document' && this.hasFilenameInputTarget) {
        this.filenameInputTarget.value = file.name
      }

      // Show file preview
      this.showFilePreview(file)
    }
  }

  showFilePreview(file) {
    // Remove existing preview
    this.removeFilePreview()

    // For audio files, show duration instead of filename
    let fileName = file.name
    if (this.currentType === 'audio' && this.audioDuration) {
      fileName = `Audio ${this.audioDuration}`
    }

    // Create file preview container inside visual textarea
    const filePreview = document.createElement('div')
    filePreview.className = 'file-preview-inline'
    filePreview.innerHTML = `
      <div class="file-preview-card-inline">
        <div class="file-preview-content">
          <div class="file-icon">
            <i class="fas ${this.getFileIcon(file)}"></i>
          </div>
          <div class="file-details">
            <div class="file-name">${fileName}</div>
            <div class="file-size">${this.formatFileSize(file.size)}</div>
          </div>
        </div>
        <button type="button" class="btn-remove-file" data-action="click->message-type#removeFile">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `

    // Insert at the beginning of visual textarea
    this.visualTextAreaTarget.insertBefore(filePreview, this.visualTextAreaTarget.firstChild)

    // Add a line break after preview
    const br = document.createElement('br')
    this.visualTextAreaTarget.insertBefore(br, filePreview.nextSibling)

    // Enable form for sending
    this.enableFormForSending()
  }

  removeFilePreview() {
    const existingPreview = this.visualTextAreaTarget.querySelector('.file-preview-inline')
    if (existingPreview) {
      // Also remove the line break after it
      const nextSibling = existingPreview.nextSibling
      if (nextSibling && nextSibling.tagName === 'BR') {
        nextSibling.remove()
      }
      existingPreview.remove()
    }
  }

  removeFile(event) {
    event.preventDefault()

    // Clear file input
    this.fileInputTarget.value = ''

    // Reset to text mode
    this.currentType = 'text'
    this.typeInputTarget.value = 'text'

    // Remove preview
    this.removeFilePreview()

    // Hide audio recorder if it was showing
    this.hideAudioRecorder()

    // Update form
    this.updateForm()
  }

  enableFormForSending() {
    // Make text area not required when there's a file
    if (this.hasTextAreaTarget) {
      this.textAreaTarget.required = false
    }

    // Update submit button to show it's ready to send
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.classList.remove('btn-secondary')
      this.submitButtonTarget.classList.add('btn-primary')
    }
  }

  getFileIcon(file) {
    const extension = file.name.split('.').pop().toLowerCase()

    if (this.currentType === 'image') {
      return 'fa-image'
    }

    if (this.currentType === 'audio') {
      return 'fa-microphone'
    }

    // Document icons based on extension
    switch(extension) {
      case 'pdf':
        return 'fa-file-pdf'
      case 'doc':
      case 'docx':
        return 'fa-file-word'
      case 'xls':
      case 'xlsx':
        return 'fa-file-excel'
      case 'ppt':
      case 'pptx':
        return 'fa-file-powerpoint'
      case 'txt':
        return 'fa-file-alt'
      case 'webm':
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'aac':
        return 'fa-microphone'
      default:
        return 'fa-file'
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Convert contenteditable content to text preserving line breaks
  getTextWithLineBreaks(element) {
    // Clone to avoid modifying original
    const clone = element.cloneNode(true)

    // Replace all <br> tags with newline characters
    const brs = clone.querySelectorAll('br')
    brs.forEach(br => {
      br.replaceWith(document.createTextNode('\n'))
    })

    // Replace block elements (div, p) that create line breaks
    // We need to handle them carefully to preserve text content
    const blockElements = Array.from(clone.querySelectorAll('div, p'))
    blockElements.forEach(block => {
      const text = block.textContent.trim()
      if (text) {
        block.replaceWith(document.createTextNode('\n' + text + '\n'))
      } else {
        block.replaceWith(document.createTextNode('\n'))
      }
    })

    // Get text content which now includes \n characters
    let text = clone.textContent || clone.innerText || ''

    // Clean up: normalize multiple consecutive newlines (max 2)
    text = text.replace(/\n{3,}/g, '\n\n')

    // Trim but preserve structure
    return text.trim()
  }
}
